import { useState, useEffect } from "react";
import SampleBankOffer from "../assets/SampleBankOffer.png";

const faqs = [
  {
    question: "Do I need to create an account?",
    answer: `No, you can freely browse all deals without creating an account 👀. 

However, signing up allows you to:
- Set personalized deal alerts 💬
- Subscribe to daily or weekly deal emails 📧 

So, an account is optional — but helpful for deal hunters! 🤑`
  },

  {
    question: "What is {brand}?",
    answer: `{standin} — a smart, shopper-friendly website 🛒 designed to help you discover the best offers, hidden savings, and personalized deals 💸 that might not be visible to everyone!

We aggregate a wide variety of deals including:

- 💳 Bank Card Offers (like Amex & BofA cashback)
- 🛍️ Weekly Ads from popular retailers
- 🏷️ YMMV-Targeted Promotions
- 🎁 Free After Rebate Deals
- 🌍 Regional & Location-Based Discounts

Every deal may vary depending on your card, location, or eligibility 😉!

{brand} is your go-to hub for stacking savings, staying ahead of limited-time promos ⏳, and never missing a money-saving opportunity again! 🤑`
  },
  {
    question: "What is special about {brand}?",
    answer: `{brand} is special because it combines product offers with bank offers, letting you stack deals and get the best prices possible! 😊

It’s a one-stop place where you can find:

- Exclusive product discounts 🛍️
- Bank-specific cashback and promos 💳
- The ability to stack and maximize your savings 🤑

Whether you’re after cashback, rebates, or limited-time sales, {brand} helps you save smarter and shop better! 🎯`
  },
  {
    question: "What is Bank Offers?",
    answer: `Bank offers are special cashback or discount deals provided by your bank for purchases at specific merchants 🏦🛍️. 

Not all bank offers are available to everyone — eligibility depends on your card, location, or account status 😉.

{brand} helps you discover these exclusive bank offers so you don’t miss out on extra savings! 💸`
  },
  {
    question: "How do Bank Offers work?",
    answer: `Bank offers are special cashback or discount deals provided by your bank or credit card company. To use these offers, you need to:

1. Log in to your bank or credit card website or app.
2. Find the relevant offer listed under your credit card benefits.
3. Add or activate the offer before making your purchase.
4. Make sure you meet the eligibility requirements, such as minimum spend amounts or product exclusions.
5. Complete your purchase using the linked credit card.

After your purchase, the cashback or discount amount will be credited back to your credit card account. Keep in mind that some bank offers have a maximum cashback limit, and some may require you to click through from the bank’s website to qualify. Be sure to read the exclusions and conditions carefully.`
  },
  {
    question: "How to find Products with Bank Offers?",
    answer: (
      <>
        <p>
          When you find a product deal, and if there are any matching bank offers for that merchant,
          you will see a <strong>flashing bank icon</strong> on the right corner. This indicates that the merchant has a bank offer too.
        </p>
        <p>
          You can click the icon to read detailed information about the bank offer.
        </p>
        <img
          src={SampleBankOffer}
          alt="Sample Bank Offer Icon"
          style={{ maxWidth: "300px", marginTop: "10px", borderRadius: "8px" }}
        />
      </>
    ),
  },
  {
    question: "How do Rewards Offers work?",
    answer: (
      <>
        <p>Rewards offers are special cash or points programs provided by select retailers. Here’s how they work for popular stores:</p>

        <h3 className="font-semibold mt-3">🏬 Walmart Cash</h3>
        <ol className="list-decimal list-inside">
          <li>Sign in to the Walmart app or website.</li>
          <li>Go to the product page and activate the Walmart Cash offer.</li>
          <li>Shop eligible items online or in-store using Walmart Pay.</li>
          <li>Rewards appear as pending, then available after the return window.</li>
          <li>Redeem Walmart Cash at checkout online or in-store.</li>
        </ol>

        <h3 className="font-semibold mt-3">🏥 CVS ExtraCare Rewards</h3>
        <ol className="list-decimal list-inside">
          <li>Sign in to the CVS app or CVS.com with your ExtraCare account.</li>
          <li>Go to the product page and activate the ExtraCare reward offer.</li>
          <li>Shop eligible items online or in-store and use your ExtraCare card or phone number at checkout.</li>
          <li>Rewards are credited to your ExtraCare account after purchase (usually within 24 hours).</li>
          <li>Redeem ExtraBucks Rewards on future purchases at checkout.</li>
        </ol>

        <h3 className="font-semibold mt-3">💊 Walgreens Cash Rewards</h3>
        <ol className="list-decimal list-inside">
          <li>Sign in to the Walgreens app or Walgreens.com with your myWalgreens account.</li>
          <li>Go to the product page and clip/activate the Walgreens Cash offer.</li>
          <li>Shop eligible items online or in-store and enter your myWalgreens number at checkout.</li>
          <li>Walgreens Cash appears in your account within 24 hours of pickup/delivery.</li>
          <li>Redeem Walgreens Cash on future purchases at checkout.</li>
        </ol>

        <h3 className="font-semibold mt-3">🖨️ Staples Easy Rewards</h3>
        <ol className="list-decimal list-inside">
          <li>Sign up free at Staples.com/rewards or in-store.</li>
          <li>Activate the offer under your Staples Rewards account (Offers tab → Click Activate).</li>
          <li>Shop the eligible item.</li>
          <li>Rewards are issued around the 15th of the following month.</li>
          <li>Redeem them like cash on future purchases (excludes gift cards, postage, etc.).</li>
          <li>Rewards expire in 2 months, so use them promptly!</li>
        </ol>

        <p className="mt-3 text-gray-700">
          Each retailer’s reward system may vary slightly, but all require activation before purchase and redemption on a future order.
        </p>
      </>
    )
  },
  {
    question: "How does the Menards rebate work?",
    answer: (
      <>
        <p>Menards rebates are special offers where you get money back after purchasing certain products. Here's how it works:</p>

        <ol>
          <li>Purchase an eligible product at Menards.</li>
          <li>Keep the rebate receipt or proof of purchase.</li>
          <li>Submit your rebate form along with the receipt to Menards by mail only — there is no online submission option.</li>
          <li>You can track your rebate status starting about 4 to 5 weeks after mailing your rebate form.</li>
          <li>The rebate check is usually sent back within 6 to 8 weeks.</li>
          <li>
            The rebate check is like store credit, can only be used in Menards stores for future purchases, and it never expires.
          </li>
        </ol>

        <p><strong>Important return policy notes:</strong></p>
        <ul>
          <li>If you return the item before the rebate is processed, the rebate will not be issued for that product.</li>
          <li>If you return the item after the rebate is processed, your refund amount will be less than the rebate issued.</li>
        </ul>

        <p>
          For more information, please visit:{" "}
          <a
            href="https://www.menards.com/main/rebates.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#007BFF", textDecoration: "underline" }}
          >
            Menards Rebates Information
          </a>
        </p>
      </>
    ),
  },
  {
    question: "How often is the site updated?",
    answer: "Our deals are updated daily, including newly released bank card offers, weekly ads, and regional promos."
  }


];

function getBrandName() {
  const hostname = window.location.hostname.replace(/^www\./i, "").toLowerCase();
  if (hostname === "dailydoseofdeal.com") return "Daily Dose of Deal";
  return "YMMV Deals";
}

function getBrandIntro(brand) {
  if (brand === "Daily Dose of Deal") {
    return "Daily Dose Of Deal";
  }
  return "YMMV stands for Your Miles May Vary";
}

function replaceBrand(text, brand) {
  if (typeof text === "string") {
    const intro = getBrandIntro(brand);
    return text
      .replace(/{brand}/g, brand)
      .replace("{standin}", intro);
  }
  return text;
}

export default function HelpPage() {
  const [openIndex, setOpenIndex] = useState(null);
  const [brand, setBrand] = useState("YMMV Deals");

  useEffect(() => {
    setBrand(getBrandName());
    window.scrollTo(0, 0);
  }, []);

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">Help & FAQs</h1>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="border rounded-md shadow">
            <button
              onClick={() => toggleIndex(index)}
              className="w-full text-left px-4 py-3 bg-gray-100 font-semibold hover:bg-gray-200"
            >
              {replaceBrand(faq.question, brand)}
            </button>
            {openIndex === index && (
              <div className="px-4 py-3 text-gray-700 whitespace-pre-line">
                {typeof faq.answer === "string"
                  ? replaceBrand(faq.answer, brand)
                  : faq.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
